import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, CreditCard, ExternalLink, PartyPopper, Receipt, Sparkles } from 'lucide-react';
import {
  useBilling,
  useCheckout,
  useInvoices,
  usePortal,
  useSyncCheckout,
  type BillingCycle,
  type BillingOverview,
  type Plan,
} from '@/api/billing';
import { useAuth } from '@/app/auth/useAuth';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  PageHeader,
  Segmented,
  Skeleton,
  useToast,
  type BadgeTone,
} from '@/components/ui';
import { cn } from '@/lib/cn';
import { formatDate, formatMoney, formatNumber } from '@/lib/format';

const STATE: Record<BillingOverview['state'], { label: string; tone: BadgeTone }> = {
  trialing: { label: 'Free trial', tone: 'brand' },
  trial_ended: { label: 'Trial ended', tone: 'red' },
  active: { label: 'Active', tone: 'green' },
  past_due: { label: 'Payment overdue', tone: 'amber' },
  expired: { label: 'Expired', tone: 'red' },
  managed: { label: 'Managed by Kollabr', tone: 'neutral' },
};

const USAGE: {
  key: keyof BillingOverview['usage'];
  limit: keyof BillingOverview['limits'];
  label: string;
}[] = [
  { key: 'seats', limit: 'seats', label: 'Team members (incl. pending invites)' },
  { key: 'widgets', limit: 'widgets', label: 'Chat widgets' },
  { key: 'aiAgents', limit: 'aiAgents', label: 'AI agents' },
  { key: 'aiRepliesThisMonth', limit: 'aiRepliesPerMonth', label: 'AI replies this month' },
  { key: 'websitePages', limit: 'websitePages', label: 'Website pages in the knowledge base' },
];

/** Settings → Billing: plan status, usage against limits, Stripe checkout / portal and invoices. */
export function BillingPage() {
  const { can } = useAuth();
  const isOwner = can('manage', 'Billing');
  const toast = useToast();
  const billing = useBilling();
  const invoices = useInvoices();
  const checkout = useCheckout();
  const portal = usePortal();
  const sync = useSyncCheckout();
  const [params, setParams] = useSearchParams();
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const handled = useRef(false);

  // Back from Stripe: apply the finished checkout straight away (the webhook follows).
  useEffect(() => {
    if (handled.current) return;
    const result = params.get('checkout');
    const sessionId = params.get('session_id');
    if (!result) return;
    handled.current = true;
    if (result === 'success' && sessionId) {
      sync.mutate(sessionId, {
        onSuccess: () => toast.success('Thank you! Your subscription is active.'),
        onError: () => toast.info('Payment received — your plan updates in a moment.'),
      });
    } else if (result === 'cancelled') {
      toast.info('Checkout cancelled — nothing was charged.');
    }
    const next = new URLSearchParams(params);
    next.delete('checkout');
    next.delete('session_id');
    setParams(next, { replace: true });
  }, [params, setParams, sync, toast]);

  if (billing.isPending) return <Skeleton className="h-[640px] rounded-2xl" />;
  if (billing.error)
    return (
      <Card>
        <ErrorState error={billing.error} onRetry={() => void billing.refetch()} />
      </Card>
    );
  const b = billing.data;
  const current = b.plans.find((p) => p.id === b.plan)!;
  const onError = (e: Error) => toast.error(e.message);

  return (
    <>
      <PageHeader
        title="Billing & plan"
        description="Your plan, what you’ve used this month, and invoices."
      />

      {params.get('welcome') && (
        <Card className="mb-5 flex items-start gap-3 border-emerald-200 bg-emerald-50/70 p-4">
          <PartyPopper className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div className="text-sm text-emerald-900">
            <p className="font-semibold">
              Your workspace is ready — the {b.daysLeft}-day free trial has started.
            </p>
            <p className="mt-0.5">
              Next: add the chat widget (Settings → Chat widget), set up an AI agent, and invite your team.
            </p>
          </div>
        </Card>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card className="p-5">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-lg font-bold text-slate-900">{current.name}</p>
            <Badge tone={STATE[b.state].tone} dot>
              {STATE[b.state].label}
            </Badge>
            {b.billingCycle && <Badge>{b.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}</Badge>}
          </div>
          <p className="mt-2 text-sm text-slate-600">{statusLine(b)}</p>
          {b.paused && (
            <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-red-200">
              The AI agent is paused and you can’t add team members, widgets or AI agents until you{' '}
              {b.state === 'trial_ended' ? 'choose a plan' : 'renew'}. Your data and live chats with your team
              keep working.
            </p>
          )}
          {b.hasSubscription && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!isOwner}
                loading={portal.isPending}
                onClick={() => portal.mutate(undefined, { onError })}
              >
                <CreditCard className="h-4 w-4" /> Manage subscription
              </Button>
              <span className="text-xs text-slate-500">
                Change plan, update your card, download invoices or cancel.
              </span>
            </div>
          )}
          {!isOwner && (
            <p className="mt-3 text-xs text-slate-500">Only the workspace owner can change the plan.</p>
          )}
        </Card>

        <Card className="p-5">
          <p className="mb-3 text-sm font-semibold text-slate-800">Usage</p>
          <ul className="space-y-3">
            {USAGE.map((u) => (
              <UsageMeter key={u.key} label={u.label} used={b.usage[u.key]} limit={b.limits[u.limit]} />
            ))}
          </ul>
        </Card>
      </div>

      <div className="mt-8 mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-slate-900">Plans</h2>
          <p className="text-sm text-slate-500">Prices in INR, excluding GST. Yearly saves two months.</p>
        </div>
        <Segmented
          label="Billing cycle"
          size="sm"
          value={cycle}
          onChange={setCycle}
          options={[
            { value: 'monthly', label: 'Monthly' },
            { value: 'yearly', label: 'Yearly' },
          ]}
        />
      </div>
      {!b.paymentsEnabled && (
        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 ring-1 ring-amber-200">
          Online payments aren’t switched on yet. Contact us to change your plan.
        </p>
      )}
      <div className="grid gap-4 md:grid-cols-3">
        {b.plans
          .filter((p) => p.id !== 'trial')
          .map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              cycle={cycle}
              current={p.id === b.plan && b.state !== 'trialing' && b.state !== 'trial_ended'}
              action={
                p.id === 'enterprise' ? (
                  <a
                    href="mailto:sales@kollabr.in?subject=Kollabr%20Support%20Enterprise"
                    className="inline-flex h-9 w-full items-center justify-center rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Talk to sales
                  </a>
                ) : b.hasSubscription ? (
                  <Button
                    variant="secondary"
                    className="w-full"
                    disabled={!isOwner || !b.paymentsEnabled || p.id === b.plan}
                    loading={portal.isPending}
                    onClick={() => portal.mutate(undefined, { onError })}
                  >
                    {p.id === b.plan ? 'Current plan' : 'Switch in portal'}
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    disabled={!isOwner || !b.paymentsEnabled}
                    loading={checkout.isPending && checkout.variables?.plan === p.id}
                    onClick={() =>
                      checkout.mutate({ plan: p.id as 'starter' | 'growth', cycle }, { onError })
                    }
                  >
                    <Sparkles className="h-4 w-4" /> Choose {p.name}
                  </Button>
                )
              }
            />
          ))}
      </div>

      <Card className="mt-8 overflow-hidden">
        <p className="flex items-center gap-2 border-b border-slate-100 px-5 py-3 text-sm font-semibold text-slate-800">
          <Receipt className="h-4 w-4 text-slate-400" /> Invoices
        </p>
        {(invoices.data ?? []).length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-500">No invoices yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {invoices.data!.map((inv) => (
              <li key={inv.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="font-medium text-slate-800">
                    {inv.number ?? inv.invoiceId} ·{' '}
                    {inv.plan ? b.plans.find((p) => p.id === inv.plan)?.name : 'Plan'}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {formatDate(inv.periodStart)} – {formatDate(inv.periodEnd)}
                  </span>
                </span>
                <span className="font-semibold text-slate-900">{formatMoney(inv.amount, inv.currency)}</span>
                <Badge tone={inv.status === 'paid' ? 'green' : 'red'}>
                  {inv.status === 'paid' ? 'Paid' : 'Failed'}
                </Badge>
                {inv.hostedInvoiceUrl && (
                  <a
                    href={inv.hostedInvoiceUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-brand-600 inline-flex items-center gap-1 text-xs font-medium hover:underline"
                  >
                    View <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

function statusLine(b: BillingOverview): string {
  const end = b.currentPeriodEnd ? formatDate(String(b.currentPeriodEnd)) : null;
  switch (b.state) {
    case 'trialing':
      return `${b.daysLeft} day${b.daysLeft === 1 ? '' : 's'} left in your free trial (ends ${formatDate(String(b.trialEndsAt))}). Choose a plan any time — you keep everything you set up.`;
    case 'trial_ended':
      return 'Your free trial has ended. Choose a plan to switch the AI agent back on.';
    case 'active':
      return b.cancelAtPeriodEnd ? `Cancelled — your plan stays active until ${end}.` : `Renews on ${end}.`;
    case 'past_due':
      return `Your last payment didn’t go through. Update your card in “Manage subscription” — access continues for a few days after ${end}.`;
    case 'expired':
      return `Your subscription ended on ${end}. Renew to switch the AI agent back on.`;
    default:
      return 'Your plan is managed by the Kollabr team. Contact us to make changes.';
  }
}

function UsageMeter({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <li>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="text-slate-600">{label}</span>
        <span className="font-medium text-slate-900 tabular-nums">
          {formatNumber(used)}{' '}
          <span className="text-slate-400">/ {limit === null ? '∞' : formatNumber(limit)}</span>
        </span>
      </div>
      {limit !== null && (
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100" aria-hidden>
          <div
            className={cn(
              'h-full rounded-full',
              pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-brand-500',
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </li>
  );
}

function PlanCard({
  plan,
  cycle,
  current,
  action,
}: {
  plan: Plan;
  cycle: BillingCycle;
  current: boolean;
  action: ReactNode;
}) {
  const price = cycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
  return (
    <Card className={cn('flex flex-col p-5', current && 'ring-brand-400 ring-2')}>
      <div className="flex items-center gap-2">
        <p className="font-bold text-slate-900">{plan.name}</p>
        {current && <Badge tone="brand">Current</Badge>}
      </div>
      <p className="mt-0.5 text-xs text-slate-500">{plan.tagline}</p>
      <p className="mt-4">
        <span className="text-2xl font-extrabold text-slate-900">{formatMoney(price, 'INR')}</span>
        <span className="text-sm text-slate-500"> / {cycle === 'yearly' ? 'year' : 'month'}</span>
      </p>
      <ul className="mt-4 flex-1 space-y-1.5 text-sm text-slate-600">
        {plan.highlights.map((h) => (
          <li key={h} className="flex items-start gap-1.5">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {h}
          </li>
        ))}
      </ul>
      <div className="mt-5">{action}</div>
    </Card>
  );
}
